import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Eye, EyeOff, User, Mail, Phone, Lock, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

const CITIES = ['دبي', 'أبوظبي', 'الشارقة', 'عجمان', 'الرياض', 'جدة', 'القاهرة', 'أخرى'];

export default function RegisterPage() {
  const { register, loading } = useAuthStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', phone: '', role: 'renter', city: ''
  });
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.password) return toast.error('يرجى ملء جميع الحقول الإلزامية');
    if (form.password.length < 6) return toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');

    const result = await register(form);
    if (result.success) {
      toast.success('تم إنشاء حسابك بنجاح!');
      navigate('/dashboard');
    } else {
      toast.error(result.message || 'حدث خطأ في التسجيل');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-900 px-4 py-8">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">SP</span>
            </div>
            <span className="text-2xl font-bold gradient-text">SPORTIX</span>
          </Link>
          <h1 className="text-2xl font-bold text-white mb-2">إنشاء حساب جديد</h1>
          <p className="text-dark-400 text-sm">انضم لأكبر منصة رياضية في المنطقة</p>
        </div>

        <div className="bg-dark-800 border border-dark-700 rounded-2xl p-6">
          {/* Role Selection */}
          <div className="grid grid-cols-3 gap-2 mb-5 p-1 bg-dark-700 rounded-xl">
            {[
              { value: 'renter', label: 'مستأجر', icon: User },
              { value: 'owner', label: 'مالك', icon: Building2 },
              { value: 'both', label: 'كلاهما', icon: null }
            ].map(({ value, label, icon: Icon }) => (
              <button key={value} type="button" onClick={() => setForm({ ...form, role: value })}
                className={`py-2 px-3 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                  form.role === value ? 'bg-primary-600 text-white' : 'text-dark-400 hover:text-dark-200'
                }`}>
                {Icon && <Icon size={12} />}{label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-dark-300 text-sm mb-1.5 block">الاسم الكامل *</label>
              <div className="relative">
                <User size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400" />
                <input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
                  placeholder="محمد أحمد" className="form-input pr-9" />
              </div>
            </div>

            <div>
              <label className="text-dark-300 text-sm mb-1.5 block">البريد الإلكتروني *</label>
              <div className="relative">
                <Mail size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400" />
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="example@email.com" className="form-input pr-9" />
              </div>
            </div>

            <div>
              <label className="text-dark-300 text-sm mb-1.5 block">رقم الهاتف</label>
              <div className="relative">
                <Phone size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400" />
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="+971 50 000 0000" className="form-input pr-9" />
              </div>
            </div>

            <div>
              <label className="text-dark-300 text-sm mb-1.5 block">المدينة</label>
              <select value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                className="form-input">
                <option value="">اختر مدينتك</option>
                {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="text-dark-300 text-sm mb-1.5 block">كلمة المرور *</label>
              <div className="relative">
                <Lock size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400" />
                <input type={showPass ? 'text' : 'password'} value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="6 أحرف على الأقل" className="form-input pr-9 pl-10" />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="btn-primary w-full justify-center py-3 text-base disabled:opacity-50">
              {loading ? 'جارٍ إنشاء الحساب...' : 'إنشاء الحساب'}
            </button>
          </form>

          <p className="text-center text-dark-400 text-sm mt-5">
            لديك حساب؟{' '}
            <Link to="/login" className="text-primary-400 hover:text-primary-300 font-semibold">تسجيل الدخول</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
